// ✅ THEME-ONLY UPDATE: FeedbackFormBuilder -> match the SAME "above theme" (StudentAttendanceByStudent / AdminFeeManagement)
// - Wrap with pageWrapSx (BG padding) + rootPaperSx (transparent + blue border + overflow hidden)
// - Header uses headerSx (orange gradient + same typography weights)
// - Body uses bodySx (BG)
// - Sections use sectionSx (white surface + blue border + LEFT orange stripe) + sectionInnerPadSx
// - Inputs use inputSx (focused label orange) + DateTimePicker slotProps
// - Buttons use primaryBtnSx + outlinedBtnSx (same as report)
// - Question cards follow section stripe + zebra tint + hover
// - Alerts/toasts: rounded + subtle blue border
// ✅ LOGIC UNCHANGED

import React from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  Grid,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TablePagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { API_URL } from "./config";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// ✅ import dropdown config
import { FEEDBACK_TYPES, CATEGORIES, PORTAL_MODULES } from "./FeedbackConfig";

const QTYPE = [
  { value: "rating", label: "Rating" },
  { value: "mcq", label: "Multiple Choice" },
  { value: "text", label: "Description" },
];

function getToken() {
  return localStorage.getItem("auth");
}

export default function FeedbackFormBuilder() {
  // ✅ Theme constants (match report pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const sectionInnerPadSx = { pl: 1 };

  // ✅ INPUTS: outline + focus label orange (exact)
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
    whiteSpace: "nowrap",
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    border: "1px solid rgba(25,118,210,0.22)",
    backgroundColor: "rgba(25,118,210,0.04)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const questionCardSx = (i) => ({
    ...sectionSx,
    p: 2,
    backgroundColor: i % 2 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
  });

  // ===== existing logic/state (unchanged) =====
  const [err, setErr] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const [toastOpen, setToastOpen] = React.useState(false);
  const [tab, setTab] = React.useState(0);
  const [promptUsed, setPromptUsed] = React.useState("");
  const [mcqCount, setMcqCount] = React.useState(3);
  const [descriptiveCount, setDescriptiveCount] = React.useState(3);
  const [ratingCount, setRatingCount] = React.useState(3);
  const [generating, setGenerating] = React.useState(false);
  const [prompts, setPrompts] = React.useState([]);
  const [promptForm, setPromptForm] = React.useState({ PromptID: null, PromptType: "FEEDBACK", PromptCategory: "FEEDBACK_FORM", PromptTitle: "", PromptPurpose: "", PromptText: "" });
  const [existingForms, setExistingForms] = React.useState([]);
  const [loadingExisting, setLoadingExisting] = React.useState(false);
  const [promptDialogOpen, setPromptDialogOpen] = React.useState(false);
  const [existingFilters, setExistingFilters] = React.useState({
    FeedbackType: "",
    Category: "",
    StartDate: "",
    EndDate: "",
    Status: "",
  });
  const [existingPage, setExistingPage] = React.useState(0);
  const [existingRowsPerPage, setExistingRowsPerPage] = React.useState(25);

  const [form, setForm] = React.useState({
    FeedbackType: "academics",
    Category: "",
    PortalModule: "",
    StartAt: null,
    EndAt: null,
    Title: "",
    Description: "",
    AllowOverallRating: 1,
    OverallRatingLabel: "Overall rating",
    IsActive: 1,
    Questions: [],
  });

  const existingCategoryOptions = React.useMemo(() => {
    if (!existingFilters.FeedbackType) {
      return [...new Set(existingForms.map((f) => f.Category).filter(Boolean))].sort();
    }
    return CATEGORIES?.[existingFilters.FeedbackType] || [];
  }, [existingFilters.FeedbackType, existingForms]);

  const filteredExistingForms = React.useMemo(() => {
    return existingForms.filter((f) => {
      if (existingFilters.FeedbackType && f.FeedbackType !== existingFilters.FeedbackType) return false;
      if (existingFilters.Category && f.Category !== existingFilters.Category) return false;

      const startDate = f.StartAt ? dayjs(f.StartAt).format("YYYY-MM-DD") : "";
      const endDate = f.EndAt ? dayjs(f.EndAt).format("YYYY-MM-DD") : "";

      if (existingFilters.StartDate && (!startDate || startDate < existingFilters.StartDate)) return false;
      if (existingFilters.EndDate && (!endDate || endDate > existingFilters.EndDate)) return false;

      if (existingFilters.Status !== "") {
        const active = Number(f.IsActive) ? "active" : "inactive";
        if (active !== existingFilters.Status) return false;
      }
      return true;
    });
  }, [existingForms, existingFilters]);

  const clearExistingFilters = () => {
    setExistingFilters({
      FeedbackType: "",
      Category: "",
      StartDate: "",
      EndDate: "",
      Status: "",
    });
    setExistingPage(0);
  };

  const existingMaxPage = Math.max(
    0,
    Math.ceil(filteredExistingForms.length / existingRowsPerPage) - 1
  );
  const safeExistingPage = Math.min(existingPage, existingMaxPage);
  const paginatedExistingForms = filteredExistingForms.slice(
    safeExistingPage * existingRowsPerPage,
    safeExistingPage * existingRowsPerPage + existingRowsPerPage
  );

  React.useEffect(() => {
    if (existingPage > existingMaxPage) {
      setExistingPage(existingMaxPage);
    }
  }, [existingPage, existingMaxPage]);

  React.useEffect(() => {
    setExistingPage(0);
  }, [
    existingFilters.FeedbackType,
    existingFilters.Category,
    existingFilters.StartDate,
    existingFilters.EndDate,
    existingFilters.Status,
  ]);

  const categoryOptions = React.useMemo(() => {
    const t = form.FeedbackType || "general";
    return CATEGORIES?.[t] || [];
  }, [form.FeedbackType]);

  React.useEffect(() => {
    const opts = categoryOptions;
    setForm((p) => {
      const nextCategory = opts.includes(p.Category) ? p.Category : opts[0] || "";
      const resetPortalModule = p.FeedbackType === "portal" ? p.PortalModule : "";
      return { ...p, Category: nextCategory, PortalModule: resetPortalModule };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.FeedbackType]);

  const toSqlDateTime = (d) => {
    if (!d || !dayjs(d).isValid()) return null;
    return dayjs(d).format("YYYY-MM-DD HH:mm:ss");
  };

  const addQuestion = (type) => {
    const q = {
      QuestionText: "",
      QuestionType: type,
      IsRequired: 1,
      SortOrder: (form.Questions.length + 1) * 10,
      RatingMin: 1,
      RatingMax: 5,
      RatingStep: 1,
      Options: type === "mcq" ? [{ OptionText: "Option 1", SortOrder: 1 }] : [],
      TextMaxLen: 500,
    };
    setForm((p) => ({ ...p, Questions: [...p.Questions, q] }));
  };

  const updateQ = (i, patch) => {
    const copy = [...form.Questions];
    copy[i] = { ...copy[i], ...patch };
    setForm((p) => ({ ...p, Questions: copy }));
  };

  const deleteQ = (i) => {
    const copy = [...form.Questions];
    copy.splice(i, 1);
    setForm((p) => ({ ...p, Questions: copy }));
  };

  const addOption = (i) => {
    const q = form.Questions[i];
    const nextIndex = (q.Options?.length || 0) + 1;
    const opts = [...(q.Options || []), { OptionText: `Option ${nextIndex}`, SortOrder: nextIndex }];
    updateQ(i, { Options: opts });
  };

  const updateOption = (qi, oi, text) => {
    const q = form.Questions[qi];
    const opts = [...(q.Options || [])];
    opts[oi] = { ...opts[oi], OptionText: text };
    updateQ(qi, { Options: opts });
  };

  const deleteOption = (qi, oi) => {
    const q = form.Questions[qi];
    const opts = [...(q.Options || [])];
    opts.splice(oi, 1);
    updateQ(qi, { Options: opts });
  };

  const authConfig = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const loadPrompts = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/feedback/builder/prompts`, authConfig());
      setPrompts(r.data || []);
    } catch {
      setPrompts([]);
    }
  }, []);

  const loadExistingForms = React.useCallback(async () => {
    setLoadingExisting(true);
    try {
      const r = await axios.get(`${API_URL}/api/feedback/builder/forms`, authConfig());
      setExistingForms(r.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load existing feedback forms");
    } finally {
      setLoadingExisting(false);
    }
  }, []);

  React.useEffect(() => {
    if (tab === 0 || tab === 1) loadPrompts();
    if (tab === 2) loadExistingForms();
  }, [tab, loadPrompts, loadExistingForms]);

  const generateFeedbackAI = async () => {
    setErr(""); setMsg("");
    if (!form.FeedbackType) return setErr("Select Feedback Type before generating questions");
    if (!form.Category) return setErr("Select Feedback Category before generating questions");
    if (!promptUsed.trim()) return setErr("Select or enter an AI prompt first");
    if ([mcqCount, descriptiveCount, ratingCount].some((x) => Number(x) < 0)) return setErr("Question counts cannot be negative");
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/feedback/builder/generate`, {
        PromptUsed: promptUsed,
        McqCount: Number(mcqCount) || 0,
        DescriptiveCount: Number(descriptiveCount) || 0,
        RatingCount: Number(ratingCount) || 0,
        FeedbackType: form.FeedbackType,
        Category: form.Category,
        PortalModule: form.PortalModule,
        ExistingTitle: form.Title,
        ExistingDescription: form.Description,
      }, authConfig());
      const d = r.data || {};
      setForm((p) => ({
        ...p,
        Title: d.Title || p.Title,
        Description: d.Description || p.Description,
        Questions: Array.isArray(d.Questions)
          ? d.Questions
              .map((q, i) => ({
                QuestionText: q.QuestionText || "",
                QuestionType: ["rating", "mcq", "text"].includes(String(q.QuestionType || "").toLowerCase()) ? String(q.QuestionType).toLowerCase() : "text",
                IsRequired: q.IsRequired === 0 ? 0 : 1,
                SortOrder: q.SortOrder ?? (i + 1) * 10,
                RatingMin: q.RatingMin ?? 1,
                RatingMax: q.RatingMax ?? 5,
                RatingStep: q.RatingStep ?? 1,
                RatingLabelMin: q.RatingLabelMin ?? "",
                RatingLabelMax: q.RatingLabelMax ?? "",
                TextMaxLen: q.TextMaxLen ?? 500,
                Options: Array.isArray(q.Options) ? q.Options.map((o, oi) => ({ OptionText: typeof o === "string" ? o : (o.OptionText || ""), SortOrder: oi + 1 })) : [],
              }))
              // Always keep descriptive/text questions at the end.
              .sort((a, b) => (a.QuestionType === "text" ? 1 : 0) - (b.QuestionType === "text" ? 1 : 0))
              .map((q, i) => ({ ...q, SortOrder: (i + 1) * 10 }))
          : p.Questions,
      }));
      setMsg("AI questions generated. Review and edit them below, then publish the feedback form.");
      setToastOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.response?.data?.message || "AI generation failed");
      setToastOpen(true);
    } finally { setGenerating(false); }
  };

  const savePrompt = async () => {
    if (!promptForm.PromptTitle.trim() || !promptForm.PromptText.trim()) return setErr("Prompt title and prompt text are required");
    try {
      const url = promptForm.PromptID ? `${API_URL}/api/feedback/builder/prompts/${promptForm.PromptID}` : `${API_URL}/api/feedback/builder/prompts`;
      const method = promptForm.PromptID ? "put" : "post";
      await axios[method](url, promptForm, authConfig());
      setPromptForm({ PromptID: null, PromptType: "FEEDBACK", PromptCategory: "FEEDBACK_FORM", PromptTitle: "", PromptPurpose: "", PromptText: "" });
      await loadPrompts();
      setMsg("Prompt saved"); setToastOpen(true);
    } catch (e) { setErr(e?.response?.data?.error || "Failed to save prompt"); setToastOpen(true); }
  };

  const deletePrompt = async (id) => {
    try { await axios.delete(`${API_URL}/api/feedback/builder/prompts/${id}`, authConfig()); await loadPrompts(); }
    catch (e) { setErr(e?.response?.data?.error || "Failed to delete prompt"); setToastOpen(true); }
  };

  const loadExistingFormForCopy = async (formId) => {
    try {
      const r = await axios.get(`${API_URL}/api/feedback/builder/forms/${formId}`, authConfig());
      const d = r.data || {}; const f = d.form || {};
      setForm((p) => ({ ...p, FeedbackType: f.FeedbackType || p.FeedbackType, Category: f.Category || p.Category,
        PortalModule: f.PortalModule || "", Title: f.Title ? `${f.Title} - Copy` : "", Description: f.Description || "",
        AllowOverallRating: Number(f.AllowOverallRating) ? 1 : 0, OverallRatingLabel: f.OverallRatingLabel || "Overall rating",
        StartAt: null, EndAt: null, IsActive: 1,
        Questions: (d.questions || [])
          .map((q) => ({ ...q, QuestionID: undefined,
            Options: (q.Options || []).map((o, oi) => ({ OptionText: o.OptionText || "", SortOrder: oi + 1 })) }))
          .sort((a, b) => (a.QuestionType === "text" ? 1 : 0) - (b.QuestionType === "text" ? 1 : 0))
          .map((q, i) => ({ ...q, SortOrder: (i + 1) * 10 })) }));
      setTab(0); setMsg("Existing feedback copied. Update dates/questions and publish when ready."); setToastOpen(true);
    } catch (e) { setErr(e?.response?.data?.message || "Failed to copy feedback form"); setToastOpen(true); }
  };

  const save = async () => {
    setErr("");
    setMsg("");

    const token = getToken();
    if (!token) {
      setErr("Session expired. Please login again.");
      setToastOpen(true);
      return;
    }

    if (!form.FeedbackType) return setErr("Feedback Type is required");
    if (!form.Category) return setErr("Feedback Category is required");
    if (!form.Title.trim()) return setErr("Title is required");
    if (!form.Questions.length) return setErr("Add at least one question");
    if (!form.StartAt) return setErr("StartAt is required");
    if (!form.EndAt) return setErr("EndAt is required");

    if (dayjs(form.EndAt).isBefore(dayjs(form.StartAt))) {
      return setErr("EndAt must be after StartAt");
    }

    const payload = {
      ...form,
      PortalModule: form.FeedbackType === "portal" ? form.PortalModule : "",
      StartAt: toSqlDateTime(form.StartAt),
      EndAt: toSqlDateTime(form.EndAt),
    };

    try {
      const resp = await axios.post(`${API_URL}/api/feedback/forms`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMsg(`Form created. FormID: ${resp.data?.FormID}`);
      setToastOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.message || "Create failed");
      setToastOpen(true);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Header bar (exact) */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Feedback Form Builder
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Create a feedback form with rating, MCQ, and description questions
            </Typography>
          </Box>

          {tab === 0 ? (
            <Button variant="contained" onClick={save} sx={primaryBtnSx}>
              Publish Feedback
            </Button>
          ) : null}
        </Box>

        {/* ✅ Body */}
        <Box sx={bodySx}>
          <Paper variant="outlined" sx={{ ...surfaceSx, mb: 2, overflow: "hidden" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
              sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 900 }, "& .Mui-selected": { color: `${THEME_ORANGE} !important` }, "& .MuiTabs-indicator": { backgroundColor: THEME_ORANGE } }}>
              <Tab icon={<AddCircleOutlineIcon />} iconPosition="start" label="Create Feedback" />
              <Tab icon={<SaveAsIcon />} iconPosition="start" label="Save Prompt" />
              <Tab icon={<ContentCopyIcon />} iconPosition="start" label="Create from Existing" />
            </Tabs>
          </Paper>
          {/* Alerts (same border+radius style) */}
          {msg ? (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
              {msg}
            </Alert>
          ) : null}
          {err ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}>
              {err}
            </Alert>
          ) : null}

          {tab === 0 ? <>
          {/* Combined Form Details + AI Feedback Generator */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>
                Form Details & AI Feedback Generator
              </Typography>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth select size="small" label="Feedback Type"
                      value={form.FeedbackType}
                      onChange={(e) => setForm((p) => ({ ...p, FeedbackType: e.target.value }))}
                      sx={inputSx}
                    >
                      {FEEDBACK_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth select size="small" label="Feedback Category"
                      value={form.Category}
                      onChange={(e) => setForm((p) => ({ ...p, Category: e.target.value }))}
                      disabled={!categoryOptions.length}
                      sx={inputSx}
                    >
                      {categoryOptions.map((c) => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {form.FeedbackType === "portal" ? (
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth select size="small" label="Portal Module"
                        value={form.PortalModule || ""}
                        onChange={(e) => setForm((p) => ({ ...p, PortalModule: e.target.value }))}
                        sx={inputSx}
                      >
                        <MenuItem value="">(Optional)</MenuItem>
                        {PORTAL_MODULES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </TextField>
                    </Grid>
                  ) : null}

                  <Grid item xs={12} md={3}>
                    <DateTimePicker
                      label="Start At"
                      value={form.StartAt}
                      onChange={(newValue) => setForm((p) => ({ ...p, StartAt: newValue }))}
                      slotProps={{ textField: { fullWidth: true, size: "small", sx: inputSx } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <DateTimePicker
                      label="End At"
                      value={form.EndAt}
                      onChange={(newValue) => setForm((p) => ({ ...p, EndAt: newValue }))}
                      slotProps={{ textField: { fullWidth: true, size: "small", sx: inputSx } }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth size="small" label="Title"
                      value={form.Title}
                      onChange={(e) => setForm((p) => ({ ...p, Title: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth size="small" label="Description" multiline rows={2}
                      value={form.Description}
                      onChange={(e) => setForm((p) => ({ ...p, Description: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.AllowOverallRating}
                          onChange={(e) => setForm((p) => ({ ...p, AllowOverallRating: e.target.checked ? 1 : 0 }))}
                          sx={{ color: "rgba(25,118,210,0.75)", "&.Mui-checked": { color: THEME_ORANGE } }}
                        />
                      }
                      label="Allow Overall Rating"
                      sx={{ "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
                    />
                  </Grid>

                  {form.AllowOverallRating ? (
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth size="small" label="Overall Rating Label"
                        value={form.OverallRatingLabel || ""}
                        onChange={(e) => setForm((p) => ({ ...p, OverallRatingLabel: e.target.value }))}
                        sx={inputSx}
                      />
                    </Grid>
                  ) : null}

                  <Grid item xs={12}>
                    <Box sx={{ borderTop: `1px solid ${BORDER}`, mt: 0.5, pt: 2 }}>
                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 0.5 }}>
                        AI Feedback Generator
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, opacity: 0.75 }}>
                        AI will use the selected Feedback Type and Feedback Category as the primary context for generating the questions.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={10}>
                    <Box
                      onClick={() => setPromptDialogOpen(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") setPromptDialogOpen(true);
                      }}
                      sx={{
                        ...surfaceSx,
                        minHeight: 76,
                        px: 1.75,
                        py: 1.15,
                        cursor: "pointer",
                        position: "relative",
                        "&:hover": { borderColor: BORDER_HOVER, backgroundColor: "rgba(25,118,210,0.02)" },
                      }}
                    >
                      <Typography variant="caption" sx={{ color: THEME_ORANGE, fontWeight: 900 }}>
                        AI Prompt
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.25,
                          pr: 4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          whiteSpace: "pre-wrap",
                          color: promptUsed ? "text.primary" : "text.secondary",
                        }}
                      >
                        {promptUsed || "Click here to enter the AI feedback prompt..."}
                      </Typography>
                      <EditNoteIcon sx={{ position: "absolute", right: 12, top: 24, color: "rgba(25,118,210,0.85)" }} />
                    </Box>
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
                      Context: {form.FeedbackType || "Select type"} / {form.Category || "Select category"} • Click prompt to edit
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth select size="small" label="Use Saved Prompt"
                      value=""
                      onChange={(e) => setPromptUsed(e.target.value)}
                      sx={inputSx}
                    >
                      <MenuItem value="">Select saved prompt</MenuItem>
                      {prompts.map((p) => <MenuItem key={p.PromptID} value={p.PromptText}>{p.PromptTitle}</MenuItem>)}
                    </TextField>
                    <Button size="small" onClick={loadPrompts} sx={{ mt: 1, ...outlinedBtnSx }}>Refresh Prompts</Button>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" size="small" label="Number Of MCQ Questions" value={mcqCount}
                      onChange={(e) => setMcqCount(e.target.value)} inputProps={{ min: 0 }} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" size="small" label="Number Of Rating Questions" value={ratingCount}
                      onChange={(e) => setRatingCount(e.target.value)} inputProps={{ min: 0 }} sx={inputSx} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth type="number" size="small" label="Number Of Descriptive Questions" value={descriptiveCount}
                      onChange={(e) => setDescriptiveCount(e.target.value)} inputProps={{ min: 0 }} sx={inputSx} />
                  </Grid>
                  
                </Grid>
              </LocalizationProvider>

              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                disabled={generating || !form.FeedbackType || !form.Category}
                onClick={generateFeedbackAI}
                sx={{ mt: 2, ...primaryBtnSx }}
              >
                {generating ? "Generating..." : "Generate Feedback with AI"}
              </Button>
            </Box>
          </Paper>

          {/* Add question buttons (section stripe) */}
          <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
            <Box sx={sectionInnerPadSx}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Questions
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={() => addQuestion("rating")} sx={outlinedBtnSx}>
                  Add Rating Q
                </Button>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={() => addQuestion("mcq")} sx={outlinedBtnSx}>
                  Add MCQ Q
                </Button>
                <Button startIcon={<AddIcon />} variant="outlined" onClick={() => addQuestion("text")} sx={outlinedBtnSx}>
                  Add Text Q
                </Button>
              </Stack>

              {!form.Questions.length ? (
                <Typography sx={{ mt: 2, opacity: 0.7 }}>No questions added yet.</Typography>
              ) : null}
            </Box>
          </Paper>

          {/* Questions list */}
          <Stack spacing={2}>
            {form.Questions.map((q, i) => (
              <Paper key={i} variant="outlined" sx={questionCardSx(i)}>
                <Box sx={sectionInnerPadSx}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                          Q{i + 1}
                        </Typography>

                        <TextField
                          select
                          size="small"
                          label="Type"
                          value={q.QuestionType}
                          onChange={(e) => updateQ(i, { QuestionType: e.target.value })}
                          sx={{ width: 200, ...inputSx }}
                        >
                          {QTYPE.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      <IconButton sx={iconBtnSx} onClick={() => deleteQ(i)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>

                    <TextField
                      label="Question"
                      size="small"
                      value={q.QuestionText}
                      onChange={(e) => updateQ(i, { QuestionText: e.target.value })}
                      fullWidth
                      sx={inputSx}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!q.IsRequired}
                          onChange={(e) => updateQ(i, { IsRequired: e.target.checked ? 1 : 0 })}
                          sx={{
                            color: "rgba(25,118,210,0.75)",
                            "&.Mui-checked": { color: THEME_ORANGE },
                          }}
                        />
                      }
                      label="Required"
                      sx={{ "& .MuiFormControlLabel-label": { fontWeight: 800 } }}
                    />

                    {q.QuestionType === "rating" ? (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                        <TextField
                          type="number"
                          size="small"
                          label="Min"
                          value={q.RatingMin ?? 1}
                          onChange={(e) => updateQ(i, { RatingMin: Number(e.target.value) })}
                          sx={inputSx}
                        />
                        <TextField
                          type="number"
                          size="small"
                          label="Max"
                          value={q.RatingMax ?? 5}
                          onChange={(e) => updateQ(i, { RatingMax: Number(e.target.value) })}
                          sx={inputSx}
                        />
                        <TextField
                          type="number"
                          size="small"
                          label="Step"
                          value={q.RatingStep ?? 1}
                          onChange={(e) => updateQ(i, { RatingStep: Number(e.target.value) })}
                          sx={inputSx}
                        />
                      </Stack>
                    ) : null}

                    {q.QuestionType === "mcq" ? (
                      <Box>
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center">
                          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Options</Typography>
                          <Button
                            size="small"
                            onClick={() => addOption(i)}
                            sx={{ textTransform: "none", fontWeight: 900, ...outlinedBtnSx, py: 0.25 }}
                          >
                            Add Option
                          </Button>
                        </Stack>

                        {(q.Options || []).map((o, oi) => (
                          <Stack key={oi} direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                              size="small"
                              value={o.OptionText}
                              onChange={(e) => updateOption(i, oi, e.target.value)}
                              fullWidth
                              sx={inputSx}
                            />
                            <IconButton sx={iconBtnSx} onClick={() => deleteOption(i, oi)}>
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        ))}
                      </Box>
                    ) : null}

                    {q.QuestionType === "text" ? (
                      <TextField
                        type="number"
                        size="small"
                        label="Max Length"
                        value={q.TextMaxLen ?? 500}
                        onChange={(e) => updateQ(i, { TextMaxLen: Number(e.target.value) })}
                        sx={inputSx}
                      />
                    ) : null}
                    
                  </Stack>
                </Box>
              </Paper>
            ))}
          </Stack>
          </> : tab === 1 ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={sectionSx}><Box sx={sectionInnerPadSx}>
                <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 2 }}>Save Prompt</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Prompt Title" value={promptForm.PromptTitle} onChange={(e) => setPromptForm((p) => ({ ...p, PromptTitle: e.target.value }))} sx={inputSx} /></Grid>
                  <Grid item xs={12} md={8}><TextField fullWidth size="small" label="Purpose" value={promptForm.PromptPurpose} onChange={(e) => setPromptForm((p) => ({ ...p, PromptPurpose: e.target.value }))} sx={inputSx} /></Grid>
                  <Grid item xs={12}><TextField fullWidth multiline minRows={5} label="Prompt Text" value={promptForm.PromptText} onChange={(e) => setPromptForm((p) => ({ ...p, PromptText: e.target.value }))} sx={inputSx} /></Grid>
                </Grid>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={savePrompt} sx={{ mt: 2, ...primaryBtnSx }}>{promptForm.PromptID ? "Update Prompt" : "Save Prompt"}</Button>
              </Box></Paper>
              {prompts.map((p) => <Paper key={p.PromptID} variant="outlined" sx={sectionSx}><Box sx={sectionInnerPadSx}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                  <Box><Typography sx={{ fontWeight: 900 }}>{p.PromptTitle}</Typography><Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{p.PromptText}</Typography></Box>
                  <Stack direction="row" spacing={1}><Button size="small" onClick={() => setPromptForm({ ...p })} sx={outlinedBtnSx}>Edit</Button><IconButton onClick={() => deletePrompt(p.PromptID)} sx={iconBtnSx}><DeleteIcon /></IconButton></Stack>
                </Stack>
              </Box></Paper>)}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={sectionSx}>
                <Box sx={sectionInnerPadSx}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Create from Existing</Typography>
                      <Typography variant="body2">Copy an existing feedback form, edit its questions, then publish it as a new form.</Typography>
                    </Box>
                    <Button onClick={loadExistingForms} sx={outlinedBtnSx}>Refresh</Button>
                  </Stack>

                  <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <FilterAltIcon sx={{ color: THEME_ORANGE }} />
                      <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>Filters</Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <TextField fullWidth select size="small" label="Feedback Type"
                          value={existingFilters.FeedbackType}
                          onChange={(e) => setExistingFilters((p) => ({ ...p, FeedbackType: e.target.value, Category: "" }))}
                          sx={inputSx}>
                          <MenuItem value="">All</MenuItem>
                          {FEEDBACK_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <TextField fullWidth select size="small" label="Feedback Category"
                          value={existingFilters.Category}
                          onChange={(e) => setExistingFilters((p) => ({ ...p, Category: e.target.value }))}
                          sx={inputSx}>
                          <MenuItem value="">All</MenuItem>
                          {existingCategoryOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <TextField fullWidth type="date" size="small" label="Start Date"
                          value={existingFilters.StartDate}
                          onChange={(e) => setExistingFilters((p) => ({ ...p, StartDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <TextField fullWidth type="date" size="small" label="End Date"
                          value={existingFilters.EndDate}
                          onChange={(e) => setExistingFilters((p) => ({ ...p, EndDate: e.target.value }))}
                          InputLabelProps={{ shrink: true }} sx={inputSx} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <TextField fullWidth select size="small" label="Status"
                          value={existingFilters.Status}
                          onChange={(e) => setExistingFilters((p) => ({ ...p, Status: e.target.value }))}
                          sx={inputSx}>
                          <MenuItem value="">All</MenuItem>
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="inactive">Inactive</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Showing {filteredExistingForms.length} of {existingForms.length} feedback forms
                      </Typography>
                      <Button onClick={clearExistingFilters} sx={outlinedBtnSx}>Clear Filters</Button>
                    </Stack>
                  </Box>
                </Box>
              </Paper>

              {loadingExisting ? (
                <CircularProgress sx={{ alignSelf: "center" }} />
              ) : filteredExistingForms.length ? (
                paginatedExistingForms.map((f) => (
                  <Paper key={f.FormID} variant="outlined" sx={sectionSx}>
                    <Box sx={sectionInnerPadSx}>
                      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>{f.Title}</Typography>
                          <Typography variant="body2">
                            {f.FeedbackType} • {f.Category} • {f.QuestionCount || 0} questions
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block" }}>
                            {f.StartAt ? dayjs(f.StartAt).format("YYYY-MM-DD") : "No start date"} → {f.EndAt ? dayjs(f.EndAt).format("YYYY-MM-DD") : "No end date"} • {Number(f.IsActive) ? "Active" : "Inactive"}
                          </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<ContentCopyIcon />}
                          onClick={() => loadExistingFormForCopy(f.FormID)} sx={primaryBtnSx}>
                          Use this
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Paper variant="outlined" sx={sectionSx}>
                  <Box sx={sectionInnerPadSx}>
                    <Typography sx={{ opacity: 0.7 }}>No feedback forms match the selected filters.</Typography>
                  </Box>
                </Paper>
              )}

              {!loadingExisting && filteredExistingForms.length > 0 ? (
                <Paper variant="outlined" sx={{ ...surfaceSx, overflow: "hidden" }}>
                  <TablePagination
                    component="div"
                    count={filteredExistingForms.length}
                    page={safeExistingPage}
                    onPageChange={(_, page) => setExistingPage(page)}
                    rowsPerPage={existingRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      const size = parseInt(e.target.value, 10);
                      setExistingRowsPerPage(size);
                      setExistingPage(0);
                    }}
                    rowsPerPageOptions={[
                      10, 25, 50, 100, 200, 300,
                    ]}
                    labelRowsPerPage="Rows per page"
                    showFirstButton
                    showLastButton
                  />
                </Paper>
              ) : null}
            </Stack>
          )}
          <Dialog
            open={promptDialogOpen}
            onClose={() => setPromptDialogOpen(false)}
            fullWidth
            maxWidth="md"
            PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", border: `1px solid ${BORDER}` } }}
          >
            <DialogTitle sx={{ background: `linear-gradient(90deg, ${THEME_ORANGE}, ${THEME_ORANGE_2})`, color: "#fff", fontWeight: 900 }}>
              Edit AI Feedback Prompt
              <Typography component="div" variant="body2" sx={{ mt: 0.5, opacity: 0.9, fontWeight: 400 }}>
                Context: {form.FeedbackType || "Select type"} / {form.Category || "Select category"}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: BG, pt: "24px !important", pb: 3 }}>
              <Paper variant="outlined" sx={{ ...surfaceSx, p: 2.5, borderTop: `4px solid ${THEME_ORANGE}` }}>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  minRows={7}
                  label="AI Prompt"
                  value={promptUsed}
                  onChange={(e) => setPromptUsed(e.target.value)}
                  sx={inputSx}
                />
              </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}` }}>
              <Button onClick={() => setPromptDialogOpen(false)} sx={outlinedBtnSx}>Cancel</Button>
              <Button variant="contained" onClick={() => setPromptDialogOpen(false)} sx={primaryBtnSx}>Save Prompt</Button>
            </DialogActions>
          </Dialog>

          {/* Toast (match report pages) */}
          <Snackbar
            open={toastOpen}
            autoHideDuration={3000}
            onClose={() => setToastOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={err ? "error" : "success"}
              sx={{ borderRadius: 2, border: "1px solid rgba(25,118,210,0.18)" }}
              onClose={() => setToastOpen(false)}
            >
              {err || msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
